import { Page } from "@flexidao/dto";
import * as D from "schemawax";

/**
 * Generates a localStorage key for the filters based on the page and tenantId
 *
 * @param page - The page where the user is
 * @param tenantId - The tenant id of the user
 */
const generateLocalStorageKey = ({ page, tenantId }: { page: Page; tenantId: string }): string =>
    `filters-${tenantId}-${page}`;

/**
 * Get the filters from local storage and decode them
 *
 * @param page - The page where the user is
 * @param tenantId - The tenant id of the user
 * @param filtersDecoder - The decoder for the filters
 *
 * @returns The filters from local storage or null if not found
 */
export const getFiltersFromLocalStorage = <FiltersType extends Record<string, unknown>>({
    page,
    tenantId,
    filtersDecoder,
}: {
    page: Page;
    tenantId: string;
    filtersDecoder: D.Decoder<Partial<FiltersType>>;
}): Partial<FiltersType> | null => {
    if (!tenantId) {
        return null;
    }

    const localStorageKey: string = generateLocalStorageKey({
        page,
        tenantId,
    });
    const stored: string | null = localStorage.getItem(localStorageKey);

    if (!stored) {
        return null;
    }

    try {
        const parsed = JSON.parse(stored);
        const decoded = filtersDecoder.decode(parsed);

        // If the decoder fails, that means that the data in localStorage is invalid, so we have to remove it
        if (!decoded) {
            localStorage.removeItem(localStorageKey);
            return null;
        }

        return decoded;
    } catch {
        return null;
    }
};

/**
 * Set the filters in local storage
 *
 * @param page - The page where the user is
 * @param tenantId - The tenant id of the user
 * @param filters - The filters to set in local storage
 *
 * @returns void
 */
export const setFiltersInLocalStorage = <FiltersType extends Record<string, unknown>>({
    page,
    tenantId,
    filters,
}: {
    page: Page;
    tenantId: string;
    filters: FiltersType;
}): void => {
    if (tenantId) {
        const key: string = generateLocalStorageKey({ page, tenantId });
        const val: string = JSON.stringify(filters);

        localStorage.setItem(key, val);
    }
};
